// Kit Contents - Enhanced with Search, Filters, and Load More
(function() {
  const scriptEl = document.currentScript;
  const config = scriptEl && scriptEl.dataset ? scriptEl.dataset : {};
  const SECTION_ID = config.sectionId || '';
  const PRODUCT_ID = config.productId || '';
  const storageKey = `kit-contents-${SECTION_ID}-expanded`;
  const DEBOUNCE_MS = 300;
  const PRODUCTS_PER_PAGE = parseInt(config.productsPerPage || '', 10) || 24;
  const PRODUCTS_PER_PAGE_MOBILE = parseInt(config.productsPerPageMobile || '', 10) || 12;
  const MOBILE_BREAKPOINT = parseInt(config.mobileBreakpoint || '', 10) || 750;
  
  let eventListeners = [];
  let clickTimeout = null;
  let searchTimeout = null;
  let variantReloadController = null;
  let pendingVariantReloadId = '';
  let currentVisibleLimit = 0;
  let currentSearchTerm = '';
  let currentCategory = 'all';
  let currentVendor = 'all';

  // State
  let productCards = [];
  let totalProducts = 0;
  
  const DEBUG_MODE = config.debugMode === 'true';

  function isEnabled(value) {
    return value === true || value === 'true';
  }

  function debugLog(...args) {
    if (DEBUG_MODE) {
      console.log('[Kit Contents Debug]', ...args);
    }
  }

  function parseVariantIds(value) {
    if (!value) return [];
    return String(value)
      .split(',')
      .map(id => id.trim())
      .filter(Boolean);
  }

  function getUnresolvedVariantIds(section) {
    const allVariantIds = parseVariantIds(section.dataset.allVariantIds);
    const jsonVariantIds = parseVariantIds(section.dataset.kitVariantIds);
    const resolvedVariantIds = new Set(parseVariantIds(section.dataset.resolvedVariantIds));
    const mergedVariantIds = Array.from(new Set([...allVariantIds, ...jsonVariantIds]));

    return mergedVariantIds.filter(id => !resolvedVariantIds.has(id));
  }

  function reportUnresolvedKitVariants(section) {
    const unresolvedVariantIds = getUnresolvedVariantIds(section);

    if (unresolvedVariantIds.length === 0) {
      debugLog('All kit variants resolved by Liquid; no browser-side product fetch needed');
      return;
    }

    section.dataset.unresolvedVariantIds = unresolvedVariantIds.join(',');
    debugLog('Kit variants not resolved by Liquid:', unresolvedVariantIds);

    if (DEBUG_MODE) {
      console.warn(
        '[Kit Contents] Some kit variants were not resolved by Liquid. Fix the variant_kit_contents metafield or enable a purpose-built endpoint before relying on browser-side loading.',
        unresolvedVariantIds
      );
    }
  }

  function initKitContents() {
    const section = document.querySelector(`[data-section-id="${SECTION_ID}"]`);

    if (!section) {
      console.warn('[Kit Contents] Section not found:', SECTION_ID);
      return;
    }

    cleanup();

    // Cache product cards
    productCards = Array.from(section.querySelectorAll('.kit-contents__product-card'));
    totalProducts = productCards.length;

    // Debug logging
    debugLog('=== Kit Contents Initialized ===');
    debugLog('Section ID:', SECTION_ID);
    debugLog('Total product cards in DOM:', totalProducts);
    debugLog('Products per page (desktop):', PRODUCTS_PER_PAGE);
    debugLog('Products per page (mobile):', PRODUCTS_PER_PAGE_MOBILE);
    debugLog('Mobile breakpoint:', MOBILE_BREAKPOINT);
    debugLog('Current viewport width:', window.innerWidth);

    // Log each card's data
    if (DEBUG_MODE && productCards.length > 0) {
      console.group('[Kit Contents Debug] Card Details');
      productCards.forEach((card, index) => {
        console.log(`Card ${index + 1}:`, {
          productId: card.dataset.productId,
          category: card.dataset.category,
          vendor: card.dataset.vendor,
          visible: card.dataset.visible,
          matchesFilter: card.dataset.matchesFilter,
          displayStyle: window.getComputedStyle(card).display
        });
      });
      console.groupEnd();
    }
    
    // Set initial visible limit based on viewport
    currentVisibleLimit = window.innerWidth <= MOBILE_BREAKPOINT ? PRODUCTS_PER_PAGE_MOBILE : PRODUCTS_PER_PAGE;

    if (isEnabled(config.enableMobileShowMore)) initShowMore(section);
    
    if (isEnabled(config.enableSearch)) initSearch(section);
    
    if (isEnabled(config.enableCategoryFilters)) {
      initFilters(section);
      initFilterDropdown(section);
    }

    // Initialize vendor filter
    initVendorDropdown(section);

    // Initialize sorting
    initSortDropdown(section);

    if (isEnabled(config.enableLoadMore)) initLoadMore(section);

    initClearFilters(section);
    initFiltersScroll(section);

    if (isEnabled(config.useTabs)) initTabs(section);

    // Initialize variant picker
    initVariantPicker(section);

    // Initialize category collapse
    initCategoryCollapse(section);

    // Position tooltips based on card position in grid
    positionTooltips(section);

    // Report unresolved metafield data without fetching the whole catalog in the browser.
    if (DEBUG_MODE) reportUnresolvedKitVariants(section);

    // Initial visibility update
    updateVisibility(section);
  }

  // Tooltip positioning - detect edge cards and adjust tooltip position
  function positionTooltips(section) {
    const grid = section.querySelector('.kit-contents__grid');
    if (!grid) return;

    const cards = grid.querySelectorAll('.kit-contents__product-card');
    if (!cards.length) return;

    // Get computed grid columns
    const gridStyle = window.getComputedStyle(grid);
    const gridColumns = gridStyle.gridTemplateColumns.split(' ').length;

    cards.forEach((card, index) => {
      const tooltip = card.querySelector('.kit-contents__tooltip');
      if (!tooltip) return;

      // Remove existing position classes
      tooltip.classList.remove('kit-contents__tooltip--left', 'kit-contents__tooltip--right');

      // Calculate column position (0-indexed)
      const columnIndex = index % gridColumns;
      const isFirstColumn = columnIndex === 0;
      const isLastColumn = columnIndex === gridColumns - 1;

      // Apply position class based on column
      if (isFirstColumn) {
        tooltip.classList.add('kit-contents__tooltip--left');
      } else if (isLastColumn) {
        tooltip.classList.add('kit-contents__tooltip--right');
      }
    });
  }

  // Category collapse functionality
  function initCategoryCollapse(section) {
    const categoryHeaders = section.querySelectorAll('.kit-contents__category-header:not(:disabled)');
    if (!categoryHeaders.length) return;

    categoryHeaders.forEach(header => {
      function handleCategoryClick() {
        const group = header.closest('.kit-contents__category-group');
        if (!group) return;

        const isCollapsed = group.dataset.collapsed === 'true';
        const content = group.querySelector('.kit-contents__category-content');

        // Toggle collapsed state
        group.dataset.collapsed = isCollapsed ? 'false' : 'true';
        header.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');

        // Animate content
        if (content) {
          if (isCollapsed) {
            content.style.display = 'block';
          } else {
            content.style.display = 'none';
          }
        }
      }

      header.addEventListener('click', handleCategoryClick);
      eventListeners.push({ element: header, event: 'click', handler: handleCategoryClick });
    });
  }

  // Filter dropdown functionality
  function initFilterDropdown(section) {
    const filterDropdown = section.querySelector('[data-filter-dropdown]');
    if (!filterDropdown) return;

    function handleFilterChange(e) {
      const selectedCategory = e.target.value;
      currentCategory = selectedCategory;

      // Update product visibility
      applyFilters(section);

      // Show/hide clear button
      updateClearButtonVisibility(section);

      updateVisibility(section);
    }

    filterDropdown.addEventListener('change', handleFilterChange);
    eventListeners.push({ element: filterDropdown, event: 'change', handler: handleFilterChange });
  }

  // Vendor dropdown functionality
  function initVendorDropdown(section) {
    const vendorDropdown = section.querySelector('[data-vendor-dropdown]');
    if (!vendorDropdown) return;

    function handleVendorChange(e) {
      const selectedVendor = e.target.value;
      currentVendor = selectedVendor;

      // Update product visibility
      applyFilters(section);

      // Show/hide clear button
      updateClearButtonVisibility(section);

      updateVisibility(section);
    }

    vendorDropdown.addEventListener('change', handleVendorChange);
    eventListeners.push({ element: vendorDropdown, event: 'change', handler: handleVendorChange });
  }

  // Apply both category and vendor filters
  function applyFilters(section) {
    productCards.forEach(card => {
      const cardCategory = card.dataset.category || '';
      const cardVendor = card.dataset.vendor || '';

      const matchesCategory = currentCategory === 'all' || cardCategory === currentCategory;
      const matchesVendor = currentVendor === 'all' || cardVendor === currentVendor;

      card.dataset.matchesFilter = (matchesCategory && matchesVendor) ? 'true' : 'false';
    });
  }

  // Show/hide clear button based on active filters
  function updateClearButtonVisibility(section) {
    const clearBtn = section.querySelector('[data-clear-filters]');
    if (clearBtn) {
      const hasActiveFilters = currentCategory !== 'all' || currentVendor !== 'all' || currentSearchTerm;
      clearBtn.style.display = hasActiveFilters ? '' : 'none';
    }
  }

  // Sort dropdown functionality
  function initSortDropdown(section) {
    const sortDropdown = section.querySelector('[data-sort-dropdown]');
    if (!sortDropdown) return;

    function handleSortChange(e) {
      const sortValue = e.target.value;
      const grid = section.querySelector('.kit-contents__grid');
      if (!grid) return;

      // Get all product cards
      const cards = Array.from(grid.querySelectorAll('.kit-contents__product-card'));
      if (cards.length === 0) return;

      // Sort cards based on selection
      cards.sort((a, b) => {
        const aTitle = a.querySelector('.kit-contents__product-title')?.textContent?.trim() || '';
        const bTitle = b.querySelector('.kit-contents__product-title')?.textContent?.trim() || '';
        const aCategory = a.dataset.category || '';
        const bCategory = b.dataset.category || '';
        const aVendor = a.querySelector('.kit-contents__vendor')?.textContent?.trim() || '';
        const bVendor = b.querySelector('.kit-contents__vendor')?.textContent?.trim() || '';
        const aIndex = parseInt(a.dataset.productIndex, 10) || 0;
        const bIndex = parseInt(b.dataset.productIndex, 10) || 0;

        switch (sortValue) {
          case 'name-asc':
            return aTitle.localeCompare(bTitle);
          case 'name-desc':
            return bTitle.localeCompare(aTitle);
          case 'category-asc':
            return aCategory.localeCompare(bCategory) || aTitle.localeCompare(bTitle);
          case 'vendor-asc':
            return aVendor.localeCompare(bVendor) || aTitle.localeCompare(bTitle);
          default:
            return aIndex - bIndex;
        }
      });

      // Re-append cards in sorted order
      cards.forEach(card => grid.appendChild(card));

      // Update card indices for animation
      cards.forEach((card, index) => {
        card.style.setProperty('--card-index', index);
      });
    }

    sortDropdown.addEventListener('change', handleSortChange);
    eventListeners.push({ element: sortDropdown, event: 'change', handler: handleSortChange });
  }

  // Variant picker functionality - handles both dropdown and chip-based pickers
  function initVariantPicker(section) {
    const variantSelect = section.querySelector('[data-variant-select]');
    const chipsContainer = section.querySelector('[data-variant-chips]');
    const productUrl = section.dataset.productUrl;
    const currentVariantId = section.dataset.variantId;
    const variantsScript = section.querySelector('[data-product-variants]');
    let variants = [];

    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        console.error('Kit Contents: Failed to parse product variants JSON', error);
      }
    }

    function updateBrowserUrl(variantId) {
      if (!variantId) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    function syncMainMcPicker(newVariantId) {
      const mainPicker = document.querySelector('[data-mc-variant-picker][data-product-id="' + PRODUCT_ID + '"]');
      if (!mainPicker || typeof mainPicker.mcSetVariantById !== 'function') return false;

      return mainPicker.mcSetVariantById(newVariantId, {
        source: 'kit-contents',
        updateUrl: true
      });
    }

    function syncNativeVariantPicker(newVariantId) {
      const targetVariant = variants.find(function(variant) {
        return String(variant.id) === String(newVariantId);
      });
      const nativePicker = document.querySelector('variant-picker[data-product-id="' + PRODUCT_ID + '"]');

      if (!targetVariant || !nativePicker) return false;

      const desiredOptions = targetVariant.options || [targetVariant.option1, targetVariant.option2, targetVariant.option3].filter(Boolean);
      let lastChangedInput = null;

      nativePicker.querySelectorAll('fieldset').forEach(function(fieldset, index) {
        const desiredValue = desiredOptions[index];
        if (!desiredValue) return;

        const targetInput = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(function(input) {
          return input.value === desiredValue;
        });

        if (!targetInput || targetInput.checked) return;

        targetInput.checked = true;
        if (typeof nativePicker.updateSelectedOption === 'function') {
          nativePicker.updateSelectedOption(targetInput);
        }
        lastChangedInput = targetInput;
      });

      nativePicker.querySelectorAll('select').forEach(function(select, index) {
        const desiredValue = desiredOptions[index];
        if (!desiredValue || select.value === desiredValue) return;

        select.value = desiredValue;
        if (typeof nativePicker.updateSelectedOption === 'function') {
          nativePicker.updateSelectedOption(select);
        }
        lastChangedInput = select;
      });

      if (!lastChangedInput) {
        updateBrowserUrl(newVariantId);
        return true;
      }

      lastChangedInput.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    function syncProductPageVariant(newVariantId) {
      return syncMainMcPicker(newVariantId) || syncNativeVariantPicker(newVariantId);
    }

    // Helper to reload section with new variant
    function reloadWithVariant(newVariantId) {
      if (!newVariantId || newVariantId === currentVariantId || !productUrl) return;

      // Show loading state
      section.style.opacity = '0.6';
      if (variantSelect) variantSelect.disabled = true;
      if (chipsContainer) chipsContainer.classList.add('kit-contents__variant-chips--loading');

      // Disable variant buttons during load
      const variantBtns = section.querySelectorAll('[data-variant-btn]');
      variantBtns.forEach(btn => btn.disabled = true);

      if (syncProductPageVariant(newVariantId)) {
        updateBrowserUrl(newVariantId);
        return;
      }

      if (variantReloadController) {
        variantReloadController.abort();
      }

      pendingVariantReloadId = newVariantId;
      variantReloadController = new AbortController();

      // Use Section Rendering API to reload just this section
      updateBrowserUrl(newVariantId);
      const sectionUrl = `${productUrl}?variant=${newVariantId}&section_id=${SECTION_ID}`;

      fetch(sectionUrl, { signal: variantReloadController.signal })
        .then(response => {
          if (!response.ok) throw new Error('Section request failed: ' + response.status);
          return response.text();
        })
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newSection = doc.querySelector(`[data-section-id="${SECTION_ID}"]`);

          if (newSection) {
            cleanup();
            section.replaceWith(newSection);
            setTimeout(initKitContents, 50);
          } else {
            // Reset if no new section found
            resetLoadingState();
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.error('Kit Contents: Failed to update section', err);
            resetLoadingState();
          }
        })
        .finally(() => {
          if (pendingVariantReloadId === newVariantId) {
            pendingVariantReloadId = '';
          }
        });
    }

    function resetLoadingState() {
      section.style.opacity = '1';
      if (variantSelect) variantSelect.disabled = false;
      if (chipsContainer) chipsContainer.classList.remove('kit-contents__variant-chips--loading');

      // Re-enable variant buttons
      const variantBtns = section.querySelectorAll('[data-variant-btn]');
      variantBtns.forEach(btn => {
        if (!btn.classList.contains('kit-contents__variant-btn--disabled')) {
          btn.disabled = false;
        }
      });
    }

    // Initialize dropdown picker
    if (variantSelect) {
      function handleVariantPickerChange(e) {
        reloadWithVariant(e.target.value);
      }
      variantSelect.addEventListener('change', handleVariantPickerChange);
      eventListeners.push({ element: variantSelect, event: 'change', handler: handleVariantPickerChange });
    }

    // Initialize chip-based variant picker
    const variantChips = section.querySelectorAll('[data-variant-chip]');
    if (variantChips.length > 0) {
      variantChips.forEach(chip => {
        function handleChipClick(e) {
          e.preventDefault();
          if (chip.disabled) return;
          variantChips.forEach(c => c.classList.remove('kit-contents__variant-chip--active'));
          chip.classList.add('kit-contents__variant-chip--active');
          if (variantSelect) {
            variantSelect.value = chip.dataset.variantChip;
          }
          reloadWithVariant(chip.dataset.variantChip);
        }
        chip.addEventListener('click', handleChipClick);
        eventListeners.push({ element: chip, event: 'click', handler: handleChipClick });
      });
    }

    // Initialize button-based picker (buttons/pills style)
    const variantButtons = section.querySelectorAll('[data-variant-btn]');
    if (variantButtons.length > 0) {
      variantButtons.forEach(btn => {
        function handleVariantBtnClick(e) {
          e.preventDefault();
          if (btn.disabled || btn.classList.contains('kit-contents__variant-btn--disabled')) return;

          const newVariantId = btn.dataset.variantBtn;

          // Update active state immediately for better UX
          variantButtons.forEach(b => {
            b.classList.remove('kit-contents__variant-btn--active');
            b.setAttribute('aria-checked', 'false');
          });
          btn.classList.add('kit-contents__variant-btn--active');
          btn.setAttribute('aria-checked', 'true');

          // Reload section with new variant
          reloadWithVariant(newVariantId);
        }
        btn.addEventListener('click', handleVariantBtnClick);
        eventListeners.push({ element: btn, event: 'click', handler: handleVariantBtnClick });
      });
    }

    // Initialize chip-based picker (legacy)
    if (chipsContainer) {
      if (!variants.length) {
        return;
      }

      const chips = chipsContainer.querySelectorAll('.kit-contents__chip');

      function handleChipClick(e) {
        const chip = e.currentTarget;
        if (chip.disabled || chip.classList.contains('kit-contents__chip--unavailable')) return;

        const optionPosition = parseInt(chip.dataset.optionPosition, 10);
        const optionValue = chip.dataset.optionValue;

        // Get currently selected options
        const selectedOptions = [];
        const optionGroups = chipsContainer.querySelectorAll('.kit-contents__option-group');

        optionGroups.forEach((group, index) => {
          if (index === optionPosition) {
            // Use the clicked chip's value
            selectedOptions.push(optionValue);
          } else {
            // Use currently selected value from this group
            const selectedChip = group.querySelector('.kit-contents__chip--selected');
            selectedOptions.push(selectedChip ? selectedChip.dataset.optionValue : '');
          }
        });

        // Find matching variant
        const matchingVariant = variants.find(variant => {
          return variant.options.every((option, index) => option === selectedOptions[index]);
        });

        if (matchingVariant) {
          // Update visual state immediately
          const siblingChips = chip.closest('.kit-contents__chips-row').querySelectorAll('.kit-contents__chip');
          siblingChips.forEach(c => c.classList.remove('kit-contents__chip--selected'));
          chip.classList.add('kit-contents__chip--selected');

          // Reload with new variant
          reloadWithVariant(matchingVariant.id);
        }
      }

      chips.forEach(chip => {
        chip.addEventListener('click', handleChipClick);
        eventListeners.push({ element: chip, event: 'click', handler: handleChipClick });
      });
    }
  }
  function initTabs(section) {
    const tabsWrapper = section.querySelector('[data-tabs-wrapper]');
    if (!tabsWrapper) return;
    
    const tabs = tabsWrapper.querySelectorAll('[role="tab"]');
    const panels = tabsWrapper.querySelectorAll('[role="tabpanel"]');
    
    if (tabs.length === 0 || panels.length === 0) return;
    
    // Handle tab click
    function handleTabClick(e) {
      const clickedTab = e.currentTarget;
      const tabId = clickedTab.dataset.tab;
      
      activateTab(tabId);
    }
    
    // Handle keyboard navigation
    function handleTabKeydown(e) {
      const currentTab = e.currentTarget;
      const tabsArray = Array.from(tabs);
      const currentIndex = tabsArray.indexOf(currentTab);
      let newIndex = -1;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          newIndex = currentIndex === 0 ? tabsArray.length - 1 : currentIndex - 1;
          break;
        case 'ArrowRight':
          e.preventDefault();
          newIndex = currentIndex === tabsArray.length - 1 ? 0 : currentIndex + 1;
          break;
        case 'Home':
          e.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          e.preventDefault();
          newIndex = tabsArray.length - 1;
          break;
      }
      
      if (newIndex !== -1) {
        tabsArray[newIndex].focus();
        activateTab(tabsArray[newIndex].dataset.tab);
      }
    }
    
    function activateTab(tabId) {
      // Deactivate all tabs
      tabs.forEach(tab => {
        tab.classList.remove('kit-contents__tab--active');
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      });
      
      // Hide all panels
      panels.forEach(panel => {
        panel.classList.remove('kit-contents__tab-panel--active');
        panel.setAttribute('hidden', '');
      });
      
      // Activate clicked tab
      const activeTab = tabsWrapper.querySelector(`[data-tab="${tabId}"]`);
      const activePanel = tabsWrapper.querySelector(`[data-tab-panel="${tabId}"]`);
      
      if (activeTab) {
        activeTab.classList.add('kit-contents__tab--active');
        activeTab.setAttribute('aria-selected', 'true');
        activeTab.setAttribute('tabindex', '0');
      }
      
      if (activePanel) {
        activePanel.classList.add('kit-contents__tab-panel--active');
        activePanel.removeAttribute('hidden');
      }
      
      // Trigger resize event for any lazy-loaded content
      window.dispatchEvent(new Event('resize'));
    }
    
    // Bind events
    tabs.forEach(tab => {
      tab.addEventListener('click', handleTabClick);
      tab.addEventListener('keydown', handleTabKeydown);
      eventListeners.push({ element: tab, event: 'click', handler: handleTabClick });
      eventListeners.push({ element: tab, event: 'keydown', handler: handleTabKeydown });
    });
  }
  function initShowMore(section) {
    const gridWrapper = section.querySelector('[data-grid-wrapper]');
    const showMoreBtn = section.querySelector('[data-show-more-btn]');

    if (!gridWrapper || !showMoreBtn) return;

    // Check saved state and restore if expanded
    try {
      const savedState = localStorage.getItem(storageKey);
      if (savedState === 'true') {
        gridWrapper.setAttribute('data-expanded', 'true');
        updateShowMoreButtonText(showMoreBtn, true);
        // Also show all items if was previously expanded
        currentVisibleLimit = totalProducts;
        updateVisibility(section);
      }
    } catch (e) {}

    const handleClick = function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (clickTimeout) clearTimeout(clickTimeout);

      clickTimeout = setTimeout(() => {
        const isExpanded = gridWrapper.getAttribute('data-expanded') === 'true';
        const newState = !isExpanded;

        gridWrapper.setAttribute('data-expanded', String(newState));
        updateShowMoreButtonText(showMoreBtn, newState);

        // Update visible limit - show all when expanded, reset to mobile limit when collapsed
        if (newState) {
          // Expanding - show all products
          currentVisibleLimit = totalProducts;
        } else {
          // Collapsing - reset to mobile page size
          currentVisibleLimit = PRODUCTS_PER_PAGE_MOBILE;
        }
        updateVisibility(section);

        try {
          localStorage.setItem(storageKey, String(newState));
        } catch (e) {}

        if (!newState) {
          const topPosition = gridWrapper.getBoundingClientRect().top + window.pageYOffset - 20;
          window.scrollTo({ top: topPosition, behavior: 'smooth' });
        }
      }, 50);
    };

    showMoreBtn.addEventListener('click', handleClick);
    eventListeners.push({ element: showMoreBtn, event: 'click', handler: handleClick });
  }

  function updateShowMoreButtonText(btn, isExpanded) {
    const showMoreText = config.showMoreText || 'Show more';
    const showLessText = config.showLessText || 'Show less';
    const textSpan = btn.querySelector('.kit-contents__show-more-text');
    if (textSpan) textSpan.textContent = isExpanded ? showLessText : showMoreText;
    btn.setAttribute('aria-label', isExpanded ? showLessText : showMoreText);
  }
  function initSearch(section) {
    const searchInput = section.querySelector('[data-search-input]');
    const searchClear = section.querySelector('[data-search-clear]');
    
    if (!searchInput) return;
    
    const handleInput = function(e) {
      const value = e.target.value;
      
      // Show/hide clear button
      if (searchClear) {
        searchClear.style.display = value.length > 0 ? 'flex' : 'none';
      }
      
      // Debounce search
      if (searchTimeout) clearTimeout(searchTimeout);
      
      searchTimeout = setTimeout(() => {
        currentSearchTerm = value.toLowerCase().trim();
        currentVisibleLimit = window.innerWidth <= MOBILE_BREAKPOINT ? PRODUCTS_PER_PAGE_MOBILE : PRODUCTS_PER_PAGE;
        updateVisibility(section);
      }, DEBOUNCE_MS);
    };
    
    const handleClear = function(e) {
      e.preventDefault();
      searchInput.value = '';
      searchClear.style.display = 'none';
      currentSearchTerm = '';
      currentVisibleLimit = window.innerWidth <= MOBILE_BREAKPOINT ? PRODUCTS_PER_PAGE_MOBILE : PRODUCTS_PER_PAGE;
      updateVisibility(section);
      searchInput.focus();
    };
    
    searchInput.addEventListener('input', handleInput);
    eventListeners.push({ element: searchInput, event: 'input', handler: handleInput });
    
    if (searchClear) {
      searchClear.addEventListener('click', handleClear);
      eventListeners.push({ element: searchClear, event: 'click', handler: handleClear });
    }
  }
  function initFilters(section) {
    const filterBtns = section.querySelectorAll('[data-filter-btn]');
    
    if (!filterBtns.length) return;
    
    filterBtns.forEach(btn => {
      const handleFilterClick = function(e) {
        e.preventDefault();
        
        const category = this.getAttribute('data-filter-btn');
        
        // Update active state
        filterBtns.forEach(b => {
          b.classList.remove('kit-contents__filter-btn--active');
          b.setAttribute('aria-pressed', 'false');
        });
        this.classList.add('kit-contents__filter-btn--active');
        this.setAttribute('aria-pressed', 'true');
        
        currentCategory = category;
        currentVisibleLimit = window.innerWidth <= MOBILE_BREAKPOINT ? PRODUCTS_PER_PAGE_MOBILE : PRODUCTS_PER_PAGE;
        updateVisibility(section);
      };
      
      btn.addEventListener('click', handleFilterClick);
      eventListeners.push({ element: btn, event: 'click', handler: handleFilterClick });
    });
  }
  function initLoadMore(section) {
    const loadMoreBtn = section.querySelector('[data-load-more-btn]');
    
    if (!loadMoreBtn) return;
    
    const handleLoadMore = function(e) {
      e.preventDefault();
      
      const increment = window.innerWidth <= MOBILE_BREAKPOINT ? PRODUCTS_PER_PAGE_MOBILE : PRODUCTS_PER_PAGE;
      currentVisibleLimit += increment;
      
      updateVisibility(section);
    };
    
    loadMoreBtn.addEventListener('click', handleLoadMore);
    eventListeners.push({ element: loadMoreBtn, event: 'click', handler: handleLoadMore });
  }
  
  function initClearFilters(section) {
    const clearBtns = section.querySelectorAll('[data-clear-filters]');

    clearBtns.forEach(btn => {
      const handleClear = function(e) {
        e.preventDefault();

        // Reset search
        const searchInput = section.querySelector('[data-search-input]');
        const searchClear = section.querySelector('[data-search-clear]');
        if (searchInput) {
          searchInput.value = '';
          if (searchClear) searchClear.style.display = 'none';
        }
        currentSearchTerm = '';

        // Reset category filter buttons (legacy)
        currentCategory = 'all';
        const filterBtns = section.querySelectorAll('[data-filter-btn]');
        filterBtns.forEach(b => {
          b.classList.remove('kit-contents__filter-btn--active');
          b.setAttribute('aria-pressed', 'false');
        });
        const allBtn = section.querySelector('[data-filter-btn="all"]');
        if (allBtn) {
          allBtn.classList.add('kit-contents__filter-btn--active');
          allBtn.setAttribute('aria-pressed', 'true');
        }

        // Reset filter dropdown
        const filterDropdown = section.querySelector('[data-filter-dropdown]');
        if (filterDropdown) {
          filterDropdown.value = 'all';
        }

        // Reset vendor dropdown
        currentVendor = 'all';
        const vendorDropdown = section.querySelector('[data-vendor-dropdown]');
        if (vendorDropdown) {
          vendorDropdown.value = 'all';
        }

        // Reset sort dropdown
        const sortDropdown = section.querySelector('[data-sort-dropdown]');
        if (sortDropdown) {
          sortDropdown.value = 'default';
          // Re-sort to default order
          const grid = section.querySelector('.kit-contents__grid');
          if (grid) {
            const cards = Array.from(grid.querySelectorAll('.kit-contents__product-card'));
            cards.sort((a, b) => {
              const aIndex = parseInt(a.dataset.productIndex, 10) || 0;
              const bIndex = parseInt(b.dataset.productIndex, 10) || 0;
              return aIndex - bIndex;
            });
            cards.forEach(card => grid.appendChild(card));
          }
        }

        // Reset product match filters
        productCards.forEach(card => {
          card.dataset.matchesFilter = 'true';
        });

        // Reset pagination
        currentVisibleLimit = window.innerWidth <= MOBILE_BREAKPOINT ? PRODUCTS_PER_PAGE_MOBILE : PRODUCTS_PER_PAGE;

        updateVisibility(section);
      };

      btn.addEventListener('click', handleClear);
      eventListeners.push({ element: btn, event: 'click', handler: handleClear });
    });
  }
  
  function initFiltersScroll(section) {
    const filtersWrapper = section.querySelector('.kit-contents__filters-wrapper');
    const filtersScroll = section.querySelector('[data-filters-scroll]');
    
    if (!filtersScroll || !filtersWrapper) return;
    
    const checkScrollPosition = function() {
      const isAtEnd = filtersScroll.scrollLeft + filtersScroll.clientWidth >= filtersScroll.scrollWidth - 10;
      const isAtStart = filtersScroll.scrollLeft <= 5;
      filtersWrapper.classList.toggle('at-end', isAtEnd);
      filtersWrapper.classList.toggle('at-start', isAtStart);
    };
    
    filtersScroll.addEventListener('scroll', checkScrollPosition, { passive: true });
    eventListeners.push({ element: filtersScroll, event: 'scroll', handler: checkScrollPosition });
    
    // Initial check
    setTimeout(checkScrollPosition, 100);
  }
  
  function updateVisibility(section) {
    const noResults = section.querySelector('[data-no-results]');
    const productCount = section.querySelector('[data-product-count]');
    const visibleCountEl = section.querySelector('[data-visible-count]');
    const totalCountEl = section.querySelector('[data-total-count]');
    const loadMoreWrapper = section.querySelector('[data-load-more-wrapper]');
    const loadMoreBtn = section.querySelector('[data-load-more-btn]');
    const clearFiltersBtn = section.querySelector('.kit-contents__clear-filters');
    const gridWrapper = section.querySelector('[data-grid-wrapper]');

    debugLog('=== updateVisibility called ===');
    debugLog('Current filters:', { search: currentSearchTerm, category: currentCategory, vendor: currentVendor });
    debugLog('Current visible limit:', currentVisibleLimit);
    debugLog('Total cards in memory:', productCards.length);

    let matchingProducts = [];
    let visibleCount = 0;

    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      // First pass: determine which products match filters
      productCards.forEach((card, index) => {
        const searchText = card.dataset.searchText || '';
        const category = card.dataset.category || 'uncategorized';
        const vendor = card.dataset.vendor || '';

        const matchesSearch = !currentSearchTerm || searchText.includes(currentSearchTerm);
        const matchesCategory = currentCategory === 'all' || category === currentCategory;
        const matchesVendor = currentVendor === 'all' || vendor === currentVendor;
        const matchesFilters = matchesSearch && matchesCategory && matchesVendor;
        
        if (matchesFilters) {
          matchingProducts.push(card);
        }
        
        card.dataset.matchesFilter = matchesFilters ? 'true' : 'false';
      });
      
      // Second pass: apply pagination to matching products
      matchingProducts.forEach((card, index) => {
        const shouldShow = index < currentVisibleLimit;
        card.dataset.visible = shouldShow ? 'true' : 'false';
        card.style.display = shouldShow ? '' : 'none';
        if (shouldShow) visibleCount++;
      });
      
      // Hide non-matching products
      productCards.forEach(card => {
        if (card.dataset.matchesFilter === 'false') {
          card.style.display = 'none';
        }
      });

      // Update UI elements
      const totalMatching = matchingProducts.length;
      const hasActiveFilters = currentSearchTerm || currentCategory !== 'all' || currentVendor !== 'all';

      // Debug summary
      debugLog('=== Visibility Update Summary ===');
      debugLog('Total matching filters:', totalMatching);
      debugLog('Currently visible:', visibleCount);
      debugLog('Hidden by pagination:', totalMatching - visibleCount);
      debugLog('Hidden by filters:', productCards.length - totalMatching);

      // Update filter title display (category and vendor)
      const filterTitleContainer = section.querySelector('[data-filter-title]');
      const categoryTitleEl = section.querySelector('[data-category-title]');
      const vendorTitleEl = section.querySelector('[data-vendor-title]');

      let showFilterTitle = false;

      // Update category title
      if (categoryTitleEl) {
        if (currentCategory !== 'all') {
          const filterDropdown = section.querySelector('[data-filter-dropdown]');
          let categoryName = currentCategory;
          if (filterDropdown) {
            const selectedOption = filterDropdown.querySelector(`option[value="${currentCategory}"]`);
            if (selectedOption) categoryName = selectedOption.textContent;
          }
          const nameEl = categoryTitleEl.querySelector('[data-category-name]');
          if (nameEl) nameEl.textContent = categoryName;
          categoryTitleEl.style.display = '';
          showFilterTitle = true;
        } else {
          categoryTitleEl.style.display = 'none';
        }
      }

      // Update vendor title
      if (vendorTitleEl) {
        if (currentVendor !== 'all') {
          const vendorDropdown = section.querySelector('[data-vendor-dropdown]');
          let vendorName = currentVendor;
          if (vendorDropdown) {
            const selectedOption = vendorDropdown.querySelector(`option[value="${currentVendor}"]`);
            if (selectedOption) vendorName = selectedOption.textContent;
          }
          const nameEl = vendorTitleEl.querySelector('[data-vendor-name]');
          if (nameEl) nameEl.textContent = vendorName;
          vendorTitleEl.style.display = '';
          showFilterTitle = true;
        } else {
          vendorTitleEl.style.display = 'none';
        }
      }

      // Show/hide main filter title container
      if (filterTitleContainer) {
        filterTitleContainer.style.display = showFilterTitle ? '' : 'none';
      }
      
      // No results message
      if (noResults) {
        noResults.style.display = totalMatching === 0 ? 'block' : 'none';
      }
      
      // Grid wrapper visibility
      if (gridWrapper) {
        gridWrapper.style.display = totalMatching === 0 ? 'none' : '';
      }
      
      // Product count
      if (visibleCountEl) visibleCountEl.textContent = visibleCount;
      if (totalCountEl) totalCountEl.textContent = totalMatching;
      if (productCount) {
        productCount.style.display = totalMatching > 0 ? '' : 'none';
      }
      
      // Load more button
      if (loadMoreWrapper) {
        const hasMore = visibleCount < totalMatching;
        loadMoreWrapper.style.display = hasMore ? '' : 'none';
      }
      
      // Clear filters button
      if (clearFiltersBtn) {
        clearFiltersBtn.style.display = hasActiveFilters ? '' : 'none';
      }

      // Search result count badge
      const searchResultCount = section.querySelector('[data-search-result-count]');
      if (searchResultCount) {
        if (currentSearchTerm) {
          searchResultCount.style.display = 'inline-flex';
          const countValue = searchResultCount.querySelector('[data-count-value]');
          if (countValue) countValue.textContent = totalMatching;
          searchResultCount.setAttribute('data-no-results', totalMatching === 0 ? 'true' : 'false');
        } else {
          searchResultCount.style.display = 'none';
        }
      }
    });
  }

  function cleanup() {
    if (clickTimeout) clearTimeout(clickTimeout);
    if (searchTimeout) clearTimeout(searchTimeout);
    if (variantReloadController) {
      variantReloadController.abort();
      variantReloadController = null;
    }
    pendingVariantReloadId = '';
    
    eventListeners.forEach(({ element, event, handler }) => {
      if (element) element.removeEventListener(event, handler);
    });
    eventListeners = [];
    productCards = [];
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initKitContents);
  } else {
    initKitContents();
  }

  // Handle resize for responsive pagination and tooltip positioning
  let resizeTimeout = null;
  window.addEventListener('resize', function() {
    if (resizeTimeout) clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const section = document.querySelector(`[data-section-id="${SECTION_ID}"]`);
      if (section) {
        const newLimit = window.innerWidth <= MOBILE_BREAKPOINT ? PRODUCTS_PER_PAGE_MOBILE : PRODUCTS_PER_PAGE;
        if (currentVisibleLimit < newLimit) {
          currentVisibleLimit = newLimit;
          updateVisibility(section);
        }
        // Reposition tooltips when grid columns change
        positionTooltips(section);
      }
    }, 150);
  });

  // Theme editor support
  document.addEventListener('shopify:section:load', function(event) {
    if (event.detail && event.detail.sectionId === SECTION_ID) {
      setTimeout(initKitContents, 50);
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    if (event.detail && event.detail.sectionId === SECTION_ID) {
      cleanup();
    }
  });

  function getVariantIdFromEvent(event) {
    if (!event) return null;

    if (event.detail) {
      if (event.detail.variant && event.detail.variant.id) return event.detail.variant.id;
      if (event.detail.resource && event.detail.resource.id) return event.detail.resource.id;
      if (event.detail.id) return event.detail.id;
    }

    if (event.target && event.target.value) return event.target.value;

    return null;
  }

  function isRelevantVariantEvent(event) {
    const eventProductId = event && event.detail && event.detail.data && event.detail.data.productId
      ? String(event.detail.data.productId)
      : '';

    return !eventProductId || eventProductId === PRODUCT_ID;
  }

  function getRenderedSectionFromEvent(event) {
    const data = event && event.detail && event.detail.data ? event.detail.data : null;
    let doc = null;

    if (!data) return null;

    if (data.sections && data.sections[SECTION_ID]) {
      doc = data.sections[SECTION_ID];
    } else if (data.sectionsHtml && typeof data.sectionsHtml[SECTION_ID] === 'string') {
      doc = new DOMParser().parseFromString(data.sectionsHtml[SECTION_ID], 'text/html');
    }

    if (!doc) return null;

    return doc.querySelector(`.kit-contents-section[data-section-id="${SECTION_ID}"]`);
  }

  function replaceWithRenderedSection(section, newSection) {
    if (!section || !newSection) return false;

    cleanup();
    section.replaceWith(newSection);
    setTimeout(initKitContents, 50);

    return true;
  }

  // Variant change detection - re-render section when variant changes
  function handleVariantChange(event) {
    if (!isRelevantVariantEvent(event)) return;

    const section = document.querySelector(`[data-section-id="${SECTION_ID}"]`);
    if (!section) return;

    const currentVariantId = String(section.dataset.variantId || '');
    const productUrl = section.dataset.productUrl;
    const newVariantId = String(getVariantIdFromEvent(event) || '');

    if (!newVariantId || newVariantId === currentVariantId || !productUrl) return;
    if (pendingVariantReloadId === newVariantId) return;

    const renderedSection = getRenderedSectionFromEvent(event);

    if (renderedSection) {
      pendingVariantReloadId = newVariantId;
      section.dataset.variantId = newVariantId;
      if (variantReloadController) {
        variantReloadController.abort();
      }
      replaceWithRenderedSection(section, renderedSection);
      pendingVariantReloadId = '';
      return;
    }

    pendingVariantReloadId = newVariantId;
    section.dataset.variantId = newVariantId;

    if (variantReloadController) {
      variantReloadController.abort();
    }

    variantReloadController = new AbortController();

    // Use Section Rendering API to reload just this section
    const sectionUrl = `${productUrl}?variant=${newVariantId}&section_id=${SECTION_ID}`;

    fetch(sectionUrl, { signal: variantReloadController.signal })
        .then(response => {
          if (!response.ok) throw new Error('Section request failed: ' + response.status);
          return response.text();
        })
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector(`[data-section-id="${SECTION_ID}"]`);

        if (newSection) {
          cleanup();
          section.replaceWith(newSection);
          setTimeout(initKitContents, 50);
        }
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Kit Contents: Failed to update section', err);
        }
      })
      .finally(() => {
        if (pendingVariantReloadId === newVariantId) {
          pendingVariantReloadId = '';
        }
      });
  }

  // Listen for common variant change events across different themes
  ['variant:update', 'variant:change', 'variantChange'].forEach(function(eventName) {
    document.addEventListener(eventName, handleVariantChange);
  });

  // Handle browser navigation where variant is in the URL
  window.addEventListener('popstate', function() {
    const variantId = new URLSearchParams(window.location.search).get('variant');
    if (variantId) {
      handleVariantChange({ detail: { variant: { id: variantId } } });
    }
  });

})();
